import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, of, BehaviorSubject } from 'rxjs';
import { take, map, switchMap, distinctUntilChanged, withLatestFrom } from 'rxjs/operators';

import { UserService } from '../global/userService';
import { HttpClientWithInFlightCache } from '../global/httpClient';
import { ArmyBuilderConfig } from '../global/config';
import { Modal } from '../global/modal';

import { selectIssues } from './support.selectors';
import { loadIssuesComplete, markIssueAsRead } from './support.actions';
import { IssueStatus, SupportIssue } from './model';
import { ForceService, selectForces } from '../forces';
import { getLog } from '../global/logging';

@Injectable()
export class SupportService {
    allIssues$: Observable<SupportIssue[]> = this.store.select(selectIssues);
    private _route$: Observable<any> = this.store.select((s: any) => s.router).pipe(distinctUntilChanged());

    public get route$(): Observable<any> {
        return this._route$;
    }

    public set route$(value: Observable<any>) {
        this._route$ = value;
    }

    unreadCount$ = new BehaviorSubject<number>(0);
    issueMetadata$ = combineLatest([this.route$, this.userService.login$]).pipe(
        withLatestFrom(this.store.select(selectForces)),
        map(([[route, userData], forces]) => {
            return [route, userData, forces || []];
        })
    );
    constructor(
        private config: ArmyBuilderConfig,
        private store: Store,
        private userService: UserService,
        private httpClient: HttpClientWithInFlightCache,
        private modal: Modal,
        private forceService: ForceService
    ) {
        this.updateUnreadCount();
    }

    updateIssue(issueId: string, update: { comment: string; issueStatus: IssueStatus }, mode: string) {
        return this.userService.login$.pipe(
            take(1),
            map((userData) => ({
                description: update.comment,
                issueStatus: update.issueStatus,
                updatedBy: userData.user.name
            })),
            switchMap((report) => {
                if (mode === 'user') {
                    return this.httpClient.post(`${this.config.apiBaseUrl}/support/${issueId}/comment`, { comment: report.description });
                }
                return this.httpClient.put(`${this.config.apiBaseUrl}/support/${issueId}`, report);
            })
        );
    }

    loadIssues(statuses: IssueStatus[] = [], numDays = 28) {
        const res = this.httpClient.get(`${this.config.apiBaseUrl}/support?statuses=${statuses.join(',')}&numDays=${numDays}`);
        res.subscribe((issues) => {
            this.store.dispatch(loadIssuesComplete({ issues }));
        });

        return res;
    }

    getIssueById(issueId: string) {
        return this.httpClient.get(`${this.config.apiBaseUrl}/support/${issueId}`).pipe(map((res) => res as SupportIssue));
    }

    async showModal() {
        // Dynamic import breaks the module-load-time circular dependency with
        // SupportComponent (which injects SupportService via constructor DI).
        const { SupportComponent } = await import('./support.component');
        this.modal.show({
            component: SupportComponent
        });
    }

    submitReport(description: string) {
        return this.issueMetadata$.pipe(
            take(1),
            map(([r, userData, forces]) => ({
                description,
                userAgent: navigator.userAgent,
                userData,
                url: r.state.url,
                appVersion: this.config.version,
                forces,
                console: getLog(),
                app: this.config.appName || 'unknown',
                source: 'in-app'
            })),
            switchMap((report) => this.httpClient.post(this.config.apiBaseUrl + '/support', report))
        );
    }

    markIssueRead(_id: string) {
        this.httpClient.put(`${this.config.apiBaseUrl}/support/${_id}/read`, null).subscribe((x) => {
            this.store.dispatch(markIssueAsRead({ issueId: _id }));
            this.updateUnreadCount();
        });
    }

    markIssueUnread(_id: string) {
        return this.httpClient.put(`${this.config.apiBaseUrl}/support/${_id}/unread`, null);
    }

    updateUnreadCount() {
        // Called unconditionally from the constructor, and SupportService is instantiated at
        // the app root — so this fires for every visitor, including one who isn't logged in
        // (e.g. browsing a public event page). Without an error handler, the 401 that follows
        // becomes an uncaught HttpErrorResponse instead of just leaving the count at 0.
        this.httpClient.get(`${this.config.apiBaseUrl}/support/unread`).subscribe({
            next: (x) => this.unreadCount$.next(parseInt(x)),
            error: () => {}
        });
    }

    deleteIssueComment(issueId: string, commentIndex: number) {
        return this.httpClient.delete(`${this.config.apiBaseUrl}/support/${issueId}/comment/${commentIndex}`);
    }
}
